import React, { useState } from 'react';
import { Plus, Minus, ArrowLeft, CheckCircle, User, ChevronLeft, ChevronRight } from 'lucide-react';

const ItemAllocator = ({ items, attendees, onItemsAllocated, onBack }) => {
  const [currentAttendeeIndex, setCurrentAttendeeIndex] = useState(0);
  const [allocatedItems, setAllocatedItems] = useState(
    items.map(item => ({
      ...item,
      allocations: attendees.reduce((acc, attendee) => {
        acc[attendee] = 0;
        return acc;
      }, {})
    }))
  );

  const updateAllocation = (itemId, attendee, change) => {
    setAllocatedItems(prev => prev.map(item => {
      if (item.id === itemId) {
        const currentAllocation = item.allocations[attendee] || 0;
        const newAllocation = Math.max(0, currentAllocation + change);
        
        return {
          ...item,
          allocations: {
            ...item.allocations,
            [attendee]: newAllocation
          }
        };
      }
      return item;
    }));
  };

  const getTotalAllocated = (item) => {
    return Object.values(item.allocations).reduce((sum, qty) => sum + qty, 0);
  };


  const isAllocationComplete = () => {
    return allocatedItems.every(item => getTotalAllocated(item) === item.quantity);
  };

  const handleContinue = () => {
    onItemsAllocated(allocatedItems);
  };

  const nextAttendee = () => {
    if (currentAttendeeIndex < attendees.length - 1) {
      setCurrentAttendeeIndex(currentAttendeeIndex + 1);
    }
  };

  const prevAttendee = () => {
    if (currentAttendeeIndex > 0) {
      setCurrentAttendeeIndex(currentAttendeeIndex - 1);
    }
  };

  const goToAttendee = (index) => {
    setCurrentAttendeeIndex(index);
  };

  const currentAttendee = attendees[currentAttendeeIndex];

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Allocate Items</h2>
        <p className="text-gray-600">Assign each item to the people who ordered it</p>
      </div>

      {/* Attendee Navigation */}
      <div className="flex justify-center">
        <div className="flex items-center space-x-2">
          {attendees.map((attendee, index) => (
            <div key={attendee} className="flex items-center">
              <button
                onClick={() => goToAttendee(index)}
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors
                  ${index === currentAttendeeIndex 
                    ? 'bg-primary-600 text-white' 
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }
                `}
              >
                {index + 1}
              </button>
              <span className={`ml-2 text-sm ${
                index === currentAttendeeIndex ? 'text-primary-600 font-medium' : 'text-gray-500'
              }`}>
                {attendee}
              </span>
              {index < attendees.length - 1 && (
                <div className={`w-8 h-0.5 mx-2 ${
                  index < currentAttendeeIndex ? 'bg-primary-600' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Current Attendee Focus */}
      <div className="card max-w-4xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <User className="h-6 w-6 text-primary-600" />
              <h3 className="text-xl font-medium text-gray-900">
                {currentAttendee}'s Allocations
              </h3>
              <span className="text-sm text-gray-500">
                ({currentAttendeeIndex + 1} of {attendees.length})
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <label htmlFor="attendee-select" className="text-sm text-gray-600">Allocate to:</label>
              <select
                id="attendee-select"
                value={currentAttendeeIndex}
                onChange={(e) => goToAttendee(Number(e.target.value))}
                className="border border-gray-300 rounded-md px-2 py-1 text-sm bg-white"
              >
                {attendees.map((attendee, index) => (
                  <option key={attendee} value={index}>{attendee}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="space-y-4">
        {allocatedItems.map((item) => (
          <div key={item.id} className="card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">{item.name}</h3>
                <p className="text-sm text-gray-500">
                  ${item.price.toFixed(2)} each • {item.quantity} total
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Allocated</p>
                <p className={`text-lg font-medium ${
                  getTotalAllocated(item) === item.quantity 
                    ? 'text-green-600' 
                    : getTotalAllocated(item) > item.quantity 
                      ? 'text-red-600' 
                      : 'text-yellow-600'
                }`}>
                  {getTotalAllocated(item)} / {item.quantity}
                </p>
              </div>
            </div>

            {/* Current attendee allocation - highlighted */}
            <div className="mb-4 p-4 bg-primary-50 border border-primary-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-primary-600" />
                  <span className="font-medium text-primary-900">{currentAttendee}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => updateAllocation(item.id, currentAttendee, -1)}
                    disabled={!item.allocations[currentAttendee]}
                    className="p-2 rounded-full bg-white border border-primary-300 hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Minus className="h-4 w-4 text-primary-600" />
                  </button>
                  <span className="w-12 text-center font-bold text-lg text-primary-900">
                    {item.allocations[currentAttendee] || 0}
                  </span>
                  <button
                    onClick={() => updateAllocation(item.id, currentAttendee, 1)}
                    className="p-2 rounded-full bg-white border border-primary-300 hover:bg-primary-50"
                  >
                    <Plus className="h-4 w-4 text-primary-600" />
                  </button>
                </div>
              </div>
            </div>

            {/* Other attendees - collapsed view */}
            <details className="group">
              <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800 mb-3">
                View all attendees ({attendees.length - 1} others)
              </summary>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {attendees.filter(attendee => attendee !== currentAttendee).map((attendee) => (
                  <div key={attendee} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700 truncate">
                      {attendee}
                    </span>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => updateAllocation(item.id, attendee, -1)}
                        disabled={!item.allocations[attendee]}
                        className="p-1 rounded-full bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Minus className="h-4 w-4 text-gray-600" />
                      </button>
                      <span className="w-8 text-center font-medium">
                        {item.allocations[attendee] || 0}
                      </span>
                      <button
                        onClick={() => updateAllocation(item.id, attendee, 1)}
                        className="p-1 rounded-full bg-white border border-gray-300 hover:bg-gray-50"
                      >
                        <Plus className="h-4 w-4 text-gray-600" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </details>

            {getTotalAllocated(item) !== item.quantity && (
              <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-700">
                  {getTotalAllocated(item) < item.quantity 
                    ? `⚠️ ${item.quantity - getTotalAllocated(item)} item(s) not allocated`
                    : `⚠️ ${getTotalAllocated(item) - item.quantity} item(s) over-allocated`
                  }
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

        <div className="flex items-center justify-between pt-6 border-t">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to attendees</span>
          </button>

          <div className="flex items-center space-x-4">
            {isAllocationComplete() && (
              <div className="flex items-center space-x-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                <span className="text-sm font-medium">All items allocated!</span>
              </div>
            )}
            
            <button
              onClick={handleContinue}
              className="btn-primary"
              disabled={!isAllocationComplete()}
            >
              Continue to summary
            </button>
          </div>
        </div>
      </div>

      {/* Attendee Navigation Controls */}
      <div className="flex items-center justify-center space-x-4">
        <button
          onClick={prevAttendee}
          disabled={currentAttendeeIndex === 0}
          className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Previous</span>
        </button>
        
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">
            {currentAttendeeIndex + 1} of {attendees.length}
          </span>
        </div>
        
        <button
          onClick={nextAttendee}
          disabled={currentAttendeeIndex === attendees.length - 1}
          className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <span>Next</span>
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="text-center">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-4xl mx-auto">
          <h3 className="font-medium text-blue-900 mb-2">Allocation tips:</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Use the attendee navigation to focus on each person's allocations</li>
            <li>• Use the + and - buttons to adjust quantities for each person</li>
            <li>• Make sure the total allocated equals the item quantity</li>
            <li>• You can split items between multiple people (e.g., 2 beers between 4 people)</li>
            <li>• All items must be fully allocated before continuing</li>
            <li>• Click on attendee numbers to jump directly to any person</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ItemAllocator;
